/**
 * BOM list. Functional spec: `qt_poc/bom_qt.py` (the list pane).
 *
 * Columns: פרויקט · דילר · סטטוס · שווי נטו · תקציב · נוצר — same order.
 */
import { useMemo, useRef, useState } from 'react'
import { ActionIcon, Button, Group, Paper, Stack, TextInput, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { BOM, MasterData } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { COMPACT_QUERY } from '../components/FilterPanel'
import { DataGrid } from '../components/DataGrid'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDate, fmtMoney } from '../format'
import { FILL_PANEL } from '../theme'
import { confirmDelete } from '../components/confirm'
import { BomEditorModal } from './BomEditorModal'

export function BomListPage() {
  const qc = useQueryClient()
  const { canEdit, canDelete } = useAuth()
  const navigate = useNavigate()
  // Same threshold as `FilterPanel` (this page has no selects to collapse,
  // just one search field, so it does not use that component — but it is
  // just as short on headroom at 1366x768/1280x720, measured 11/10 visible
  // rows against this list's own 12-row floor before this).
  const compact = useMediaQuery(COMPACT_QUERY)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const taskInput = useRef<HTMLInputElement>(null)

  const boms = useQuery({ queryKey: ['boms'], queryFn: () => api.get<BOM[]>('/boms') })

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/boms/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boms'] })
      notifications.show({ message: 'ה-BOM נמחק', color: 'green' })
    },
    // The server refuses while a PO references the BOM and names the PO in the
    // message — deleting the order first is the actual next step, so the text
    // is worth showing as-is.
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'המחיקה נכשלה',
      }),
  })

  /**
   * Copy a BOM, lines and all — `_duplicate_bom` in `qt_poc/bom_qt.py`.
   *
   * Composed from two existing calls rather than given a route of its own:
   * a duplicate is a plain POST of a BOM the client already has, and there is
   * no server-side rule about copying that the client could get wrong. The
   * dealer and snapshot come along deliberately — a copy priced against a
   * different dealer is a different BOM.
   *
   * It re-fetches by id first: the list rows do not carry `lines`, so copying
   * the row in hand would produce an empty BOM that looked right in the list.
   */
  const duplicate = useMutation({
    mutationFn: async (bom: BOM) => {
      const full = await api.get<BOM>(`/boms/${bom.id}`)
      return api.post<BOM>('/boms', {
        ...full,
        id: null,
        project_name: `${full.project_name} (עותק)`,
        status: 'טיוטה',           // a copy is always a fresh draft
        order_number: '', maor_number: '', emf_number: '',
        lines: full.lines.map((l) => ({ ...l, id: null, bom_id: null })),
      })
    },
    onSuccess: (bom) => {
      void qc.invalidateQueries({ queryKey: ['boms'] })
      notifications.show({ message: 'ה-BOM שוכפל', color: 'green' })
      navigate(`/bom/${bom.id}`)
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'השכפול נכשל',
      }),
  })

  /**
   * Import a BOM task document — the JSON produced by "ייצא משימה", which is
   * how a BOM moves between installations.
   *
   * `POST /boms/import-task` takes the parsed document as the request body,
   * not a file upload, so this reads the file in the browser. That also means
   * a malformed file fails here with a readable message instead of as a 422.
   */
  const importTask = useMutation({
    mutationFn: async (file: File) => {
      let parsed: unknown
      try {
        parsed = JSON.parse(await file.text())
      } catch {
        throw new ApiError(400, 'הקובץ אינו JSON תקין.')
      }
      return api.post<BOM>('/boms/import-task', parsed)
    },
    onSuccess: (bom) => {
      void qc.invalidateQueries({ queryKey: ['boms'] })
      notifications.show({ message: 'המשימה יובאה', color: 'green' })
      navigate(`/bom/${bom.id}`)
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'ייבוא המשימה נכשל',
      }),
  })
  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })

  const dealerName = useMemo(() => {
    const map = new Map<number, string>()
    for (const d of master.data?.dealers ?? []) if (d.id) map.set(d.id, d.name)
    return map
  }, [master.data])

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return boms.data ?? []
    return (boms.data ?? []).filter((b) => b.project_name.toLowerCase().includes(term))
  }, [boms.data, search])

  const columns = useMemo<ColumnDef<BOM, any>[]>(
    () => [
      { accessorKey: 'project_name', header: 'פרויקט' },
      {
        id: 'dealer',
        header: 'דילר',
        accessorFn: (b) => (b.dealer_id ? dealerName.get(b.dealer_id) ?? '' : ''),
        meta: { hideBelow: 'md' },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      {
        accessorKey: 'status',
        header: 'סטטוס',
        meta: { nowrap: true },
        cell: (c) => <StatusBadge value={c.getValue<string>()} />,
      },
      {
        id: 'budget',
        header: 'תקציב',
        meta: { numeric: true, hideBelow: 'md' },
        accessorFn: (b) => b.budget,
        cell: (c) =>
          c.row.original.budget > 0
            ? fmtMoney(c.row.original.budget, c.row.original.budget_mode)
            : EM_DASH,
      },
      {
        id: 'vat',
        header: 'מע"מ / משלוח',
        meta: { hideBelow: 'lg' },
        cell: (c) => `${c.row.original.vat_pct}% / ${c.row.original.shipping_pct}%`,
      },
      {
        accessorKey: 'created_at',
        header: 'נוצר',
        meta: { hideBelow: 'lg' },
        cell: (c) => fmtDate(c.getValue<string>()),
      },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {canEdit && (
              <Button
                size="xs"
                variant="subtle"
                loading={duplicate.isPending &&
                         duplicate.variables?.id === c.row.original.id}
                onClick={() => duplicate.mutate(c.row.original)}
              >
                שכפל
              </Button>
            )}
            {canDelete && (
              <ActionIcon
                size={30} color="red" variant="subtle" aria-label="מחק BOM"
                loading={remove.isPending && remove.variables === c.row.original.id}
                onClick={() => {
                  void confirmDelete(`"${c.row.original.project_name}"`).then(
                    (ok) => ok && remove.mutate(c.row.original.id!))
                }}
              >
                ✕
              </ActionIcon>
            )}
          </Group>
        ),
      },
    ],
    [dealerName, canEdit, canDelete, remove, duplicate],
  )

  return (
    <Stack gap={compact ? 8 : 'md'} h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>BOM פרויקטים</Title>
        <Group gap="xs">
          {canEdit && (
            <>
              <Button
                variant="default"
                loading={importTask.isPending}
                onClick={() => taskInput.current?.click()}
              >
                ייבא משימה (JSON)
              </Button>
              <input
                ref={taskInput}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0]
                  // Cleared so choosing the same file twice fires again.
                  e.currentTarget.value = ''
                  if (file) importTask.mutate(file)
                }}
              />
            </>
          )}
          {canEdit && <Button onClick={() => setCreating(true)}>BOM חדש</Button>}
        </Group>
      </Group>

      <Paper withBorder p={compact ? 4 : 'sm'}>
        <TextInput
          label={compact ? undefined : 'חיפוש'}
          placeholder="שם פרויקט…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ maxWidth: 320 }}
        />
      </Paper>

      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={rows}
          columns={columns}
          loading={boms.isLoading}
          empty="אין BOM להצגה"
          onRowClick={(bom) => navigate(`/bom/${bom.id}`)}
          maxHeight="100%"
        />
      </Paper>

      <BomEditorModal
        opened={creating}
        bom={null}
        master={master.data}
        onClose={() => setCreating(false)}
        onSaved={(saved) => {
          setCreating(false)
          navigate(`/bom/${saved.id}`)
        }}
      />
    </Stack>
  )
}
